// Haystack delivery for Bugsnag events, with a fixed custom-field dictionary.

/** True when Haystack dual-report applies (production, flag on, no opt-out). */
export function shouldDualReport({ isProduction, flagEnabled }) {
  if (!isProduction || !flagEnabled) {
    return false;
  }
  return getCookie("use_local_dev") !== "true";
}

/** True when events go to Haystack only. Needs a token, so reporting never goes dark. */
export function shouldReportToHaystackOnly({ isProduction, flagEnabled, authToken }) {
  if (!isProduction || !flagEnabled || !authToken) {
    return false;
  }
  return getCookie("use_local_dev") !== "true";
}

function getCookie(name) {
  if (typeof document === "undefined" || !document.cookie) {
    return null;
  }
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? decodeURIComponent(match[2]) : null;
}

// Haystack-only fields per event, kept off the Bugsnag payload.
const haystackFieldsByEvent = new WeakMap();

/** Attach Haystack-only fields to a Bugsnag event. */
export function setHaystackFields(event, fields) {
  if (!event || !fields) return;
  haystackFieldsByEvent.set(event, { ...haystackFieldsByEvent.get(event), ...prune({ ...fields }) });
}

// Custom fields copied onto the event as c_*.
const DICTIONARY_KEYS = ['handled'];

function prune (fields) {
  return Object.fromEntries(Object.entries(fields).filter(([, value]) => value !== undefined));
}

function stringifyIdFields (fields) {
  Object.keys(fields).forEach((key) => {
    if (/_id$/.test(key) && fields[key] != null) fields[key] = String(fields[key]);
  });
}

// Haystack stores parsed UA fields, not the raw string.
function parseUserAgent (userAgent) {
  if (!userAgent) return {};

  let os, osVersion;
  const androidMatch = /Android ([\d.]+)/.exec(userAgent);
  const iosMatch = /(?:iPhone|iPad|iPod).*?OS ([\d_]+)/.exec(userAgent);
  const winMatch = /Windows NT ([\d.]+)/.exec(userAgent);
  const macMatch = /Mac OS X ([\d_.]+)/.exec(userAgent);

  if (androidMatch) { os = 'Android'; osVersion = androidMatch[1]; }
  else if (iosMatch) { os = 'iOS'; osVersion = iosMatch[1].replace(/_/g, '.'); }
  else if (winMatch) { os = 'Windows'; osVersion = winMatch[1]; }
  else if (macMatch) { os = 'macOS'; osVersion = macMatch[1].replace(/_/g, '.'); }
  else if (/Linux/.test(userAgent)) { os = 'Linux'; }

  let name, version;
  const edgeMatch = /Edg\/([\d.]+)/.exec(userAgent);
  const chromeMatch = /Chrome\/([\d.]+)/.exec(userAgent);
  const firefoxMatch = /Firefox\/([\d.]+)/.exec(userAgent);
  const safariMatch = /Version\/([\d.]+).*Safari/.exec(userAgent);

  if (edgeMatch) { name = 'Edge'; version = edgeMatch[1]; }
  else if (chromeMatch) { name = 'Chrome'; version = chromeMatch[1]; }
  else if (firefoxMatch) { name = 'Firefox'; version = firefoxMatch[1]; }
  else if (safariMatch) { name = 'Safari'; version = safariMatch[1]; }

  return { name, version, os, osVersion };
}

// Placeholder for absent values.
const EMPTY_FIELD_PLACEHOLDER = "-";
const withFieldPlaceholder = (value) => (value === undefined || value === null || value === "" ? EMPTY_FIELD_PLACEHOLDER : value);

function buildMeta (appMeta) {
  const nav = typeof navigator !== 'undefined' ? navigator : {};
  const conn = nav.connection || {};
  const ua = parseUserAgent(nav.userAgent);
  return prune({
    ua_n: withFieldPlaceholder(appMeta.userAgentName || ua.name),
    ua_v: withFieldPlaceholder(appMeta.userAgentVersion || ua.version),
    ua_os: withFieldPlaceholder(appMeta.userAgentOS || ua.os),
    ua_ov: withFieldPlaceholder(appMeta.userAgentOSVersion || ua.osVersion),
    u_c: withFieldPlaceholder(conn.effectiveType),
    u_dc: nav.hardwareConcurrency, // numeric, so no text fallback
    acc_id: withFieldPlaceholder(appMeta.accountID),
    acc_region: withFieldPlaceholder(appMeta.accountRegion),
    app_type: withFieldPlaceholder(appMeta.appType),
    app_v: withFieldPlaceholder(appMeta.appVersion),
    user_id: withFieldPlaceholder(appMeta.userID),
  });
}

// Names Haystack reserves.
const RESERVED = new Set(["_tenant", "_timestamp", "_source", "_id", "product", "region", "message"]);
// Core (non c_*) fields.
const CORE = new Set(["e_ts", "msg", "err_type", "err_stack", "err_msg"]);
const MAX_OBJECT_FIELD_LENGTH = 2048;

// JSON.stringify can throw (cycles, BigInt) or return undefined (functions); fall back to String().
function safeStringify (value) {
  try {
    const json = JSON.stringify(value);
    return json === undefined ? String(value) : json;
  } catch (err) {
    return String(value);
  }
}

// Maps a Bugsnag event to Haystack's flat event shape.
function toHaystackEvent (bugsnagEvent) {
  const exception = (bugsnagEvent.errors && bugsnagEvent.errors[0]) || {};
  const request = bugsnagEvent.request || {};
  const haystackFields = haystackFieldsByEvent.get(bugsnagEvent) || {};
  // Static message for grouping, when the original has variables.
  const message = haystackFields.message || exception.errorMessage || "unknown error";
  const deviceTime = bugsnagEvent.device && bugsnagEvent.device.time;
  const event = prune({
    e_ts: (deviceTime instanceof Date ? deviceTime.getTime() : Date.parse(deviceTime)) || Date.now(),
    msg: message,
    err_type: exception.errorClass,
    // Haystack groups by err_msg.
    err_msg: exception.errorClass ? `${exception.errorClass}: ${message}` : message,
    err_stack: formatStack(exception.stacktrace),
    c_url: request.url || (typeof window !== "undefined" ? window.location?.href : undefined),
    c_context: haystackFields.context || bugsnagEvent.context,
    c_severity: haystackFields.severity || bugsnagEvent.severity,
    c_session_id: bugsnagEvent._session && bugsnagEvent._session.id,
    c_lineno: topFrameValue(exception.stacktrace, "lineNumber"),
    c_colno: topFrameValue(exception.stacktrace, "columnNumber"),
    c_breadcrumbs: serializeBreadcrumbs(bugsnagEvent.breadcrumbs),
    c_locale: bugsnagEvent.device && bugsnagEvent.device.locale,
    c_orientation: bugsnagEvent.device && bugsnagEvent.device.orientation,
    c_user_agent: bugsnagEvent.device && bugsnagEvent.device.userAgent,
    c_duration: formatDuration(bugsnagEvent.app && bugsnagEvent.app.duration),
    c_release_stage: bugsnagEvent.app && bugsnagEvent.app.releaseStage,
    c_app_type: bugsnagEvent.app && bugsnagEvent.app.type,
  });

  // Default handled state, unless the call site set one.
  const customFields = { ...haystackFields };
  if (customFields.handled === undefined && typeof bugsnagEvent.unhandled === "boolean") customFields.handled = !bugsnagEvent.unhandled;
  for (const key of DICTIONARY_KEYS) {
    if (customFields[key] === undefined) continue;
    event[`c_${key}`] = key === "handled" ? (customFields[key] ? "true" : "false") : customFields[key];
  }

  for (const fieldName of Object.keys(event)) {
    if (RESERVED.has(fieldName)) { delete event[fieldName]; continue; }
    if (!CORE.has(fieldName) && !fieldName.startsWith("c_")) { delete event[fieldName]; continue; }
    const valueType = typeof event[fieldName];
    if (valueType === "object" || valueType === "function") {
      event[fieldName] = safeStringify(event[fieldName]).slice(0, MAX_OBJECT_FIELD_LENGTH);
    }
  }

  stringifyIdFields(event);
  return prune(event);
}

function formatStack (frames) {
  if (!Array.isArray(frames)) return undefined;
  return frames.slice(0, 30).map((frame) =>
    `  at ${frame.method || "<anonymous>"} (${frame.file}:${frame.lineNumber}:${frame.columnNumber})`
  ).join("\n");
}

/** A property (such as lineNumber) of the top stack frame, where the error was thrown. */
function topFrameValue (frames, key) {
  return Array.isArray(frames) && frames[0] ? frames[0][key] : undefined;
}

function serializeBreadcrumbs (crumbs) {
  if (!Array.isArray(crumbs) || !crumbs.length) return undefined;
  return crumbs
    .map((crumb) => `${formatBreadcrumbTime(crumb.timestamp)}|${crumb.type}|${breadcrumbMessage(crumb)}`)
    .join(" ;; ");
}

/** "26ms", "6.14s", "4m 15s", "1h 2m". */
function formatDuration (ms) {
  if (typeof ms !== "number" || Number.isNaN(ms)) return undefined;
  if (ms < 1000) return `${Math.round(ms)}ms`;
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${(Math.floor(ms / 10) / 100).toFixed(2)}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

/** True for our own Haystack requests, so they aren't recorded as breadcrumbs. */
export function isHaystackRequestBreadcrumb (breadcrumb, endpoint) {
  const request = breadcrumb && breadcrumb.metadata && breadcrumb.metadata.request;
  return Boolean(endpoint) && typeof request === "string" && request.includes(endpoint);
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000; // IST is UTC+5:30 with no daylight saving

/** "Oct 10, 20:02:24 IST" — fixed offset, independent of the viewer's timezone. */
function formatBreadcrumbTime (timestamp) {
  const time = new Date(timestamp).getTime();
  if (Number.isNaN(time)) return String(timestamp);
  const istTime = new Date(time + IST_OFFSET_MS);
  const pad = (value) => String(value).padStart(2, "0");
  return `${MONTHS[istTime.getUTCMonth()]} ${istTime.getUTCDate()}, ${pad(istTime.getUTCHours())}:${pad(istTime.getUTCMinutes())}:${pad(istTime.getUTCSeconds())} IST`;
}

const REDACTED = "[redacted]";
// Keys that contain email or end in name (case and separators ignored) are redacted.
const SENSITIVE_KEY = /email|name$/;

function isSensitiveKey (key) {
  return SENSITIVE_KEY.test(String(key).replace(/[\s._-]+/g, "").toLowerCase());
}

/** A copy of `value` with sensitive keys redacted at any depth. Haystack data only. */
function redactSensitive (value, visited = new WeakSet()) {
  if (value === null || typeof value !== "object") return value;
  if (visited.has(value)) return "[circular]";
  visited.add(value);
  const result = Array.isArray(value)
    ? value.map((item) => redactSensitive(item, visited))
    : Object.fromEntries(Object.keys(value).map((key) => [key, isSensitiveKey(key) ? REDACTED : redactSensitive(value[key], visited)]));
  visited.delete(value);
  return result;
}

/** Prefer errorMessage or the request URL over Bugsnag's generic breadcrumb message. */
function breadcrumbMessage (breadcrumb) {
  const meta = breadcrumb.metadata || breadcrumb.metaData;
  if (breadcrumb.type === "error" && meta && meta.errorMessage) {
    return `${breadcrumb.message}: ${meta.errorMessage}`;
  }
  if (breadcrumb.type === "request" && meta && meta.request) {
    const status = meta.status != null ? ` (${meta.status})` : "";
    return `${breadcrumb.message}: ${meta.request}${status}`;
  }
  // Other types keep their metadata.
  if (meta && Object.keys(meta).length) {
    return `${breadcrumb.message} ${safeStringify(redactSensitive(meta))}`;
  }
  return breadcrumb.message;
}

/** One request per event. No batching or retry; failures are logged and dropped. */
export function haystackDelivery ({ endpoint, authToken, appMeta = {}, debug = false }) {
  const warn = (...args) => { if (debug) console.warn(...args); };

  // Captured per event, so each keeps its original identity.
  const snapshotMeta = () => {
    const meta = buildMeta(appMeta);
    stringifyIdFields(meta);
    return meta;
  };

  // Always resolves.
  const postEvent = (meta, event) => {
    if (!authToken) {
      warn('[haystack] no authToken configured, dropping event');
      return Promise.resolve();
    }

    const body = JSON.stringify({ meta, events: [event] });

    try {
      return fetch(endpoint, {
        method: 'POST',
        headers: { 'content-type': 'application/json', 'x-auth-token': authToken },
        body,
      }).then((res) => {
        if (!res.ok) warn('[haystack] delivery rejected', res.status);
      }).catch((err) => warn('[haystack] delivery failed', err));
    } catch (err) {
      warn('[haystack] delivery failed', err);
      return Promise.resolve();
    }
  };

  return {
    sendEvent (payload, onComplete = () => {}) {
      try {
        (payload.events || []).forEach((bugsnagEvent) => postEvent(snapshotMeta(), toHaystackEvent(bugsnagEvent)));
      } catch (err) {
        warn('[haystack] transform failed', err);
      }
      onComplete();
    },
    sendSession (_payload, onComplete = () => {}) {
      onComplete();
    },
  };
}

/** Swap a started Bugsnag client's delivery to Haystack only. Returns false and keeps Bugsnag on failure. */
export function enableHaystackOnly (client, { haystackOptions } = {}) {
  if (typeof (client && client._setDelivery) !== 'function') {
    console.warn('[haystack] Bugsnag _setDelivery not found; staying on Bugsnag');
    return false;
  }

  try {
    // _setDelivery takes a factory.
    const delivery = haystackDelivery(haystackOptions);
    client._setDelivery(() => delivery);
    return true;
  } catch (err) {
    console.warn('[haystack] haystack-only setup failed; staying on Bugsnag', err);
    return false;
  }
}

/** Dual-report. Each destination has its own try/catch; onComplete reports Bugsnag's result only. */
export function dualDelivery ({ bugsnagDelivery, haystackOptions, debug = false }) {
  const haystack = haystackDelivery({ ...haystackOptions, debug });

  const toBugsnag = (method, payload, onComplete) => {
    let completed = false;
    const complete = (err) => {
      if (completed) return;
      completed = true;
      if (err && debug) console.warn(`[dual] bugsnag ${method} failed`, err);
      onComplete(err);
    };
    try {
      bugsnagDelivery[method](payload, complete);
    } catch (err) {
      complete(err);
    }
  };

  const toHaystackSafely = (method, payload) => {
    try {
      haystack[method](payload, () => {});
    } catch (err) {
      if (debug) console.warn(`[dual] haystack ${method} threw`, err);
    }
  };

  return () => ({
    sendEvent (payload, onComplete = () => {}) {
      toBugsnag('sendEvent', payload, onComplete);
      toHaystackSafely('sendEvent', payload);
    },
    sendSession (payload, onComplete = () => {}) {
      toBugsnag('sendSession', payload, onComplete);
      toHaystackSafely('sendSession', payload);
    },
  });
}
